'use client'

import { cn } from '@brew/ui/cn'
import { Fragment, type ReactNode, useEffect, useMemo } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useSetPageTitle } from '@/contexts/page-title-context'
import { PAGE_TITLE_CLASSES } from '@/lib/shared/layout-constants'

export type DetailPageHeaderProps = {
  /** Label for the parent collection, such as API or Automations. */
  parentLabel: string
  /** Route back to the parent collection. */
  parentHref: string
  /**
   * Crumbs between the collection and the current page, for a page nested
   * under an entity (Domains / mail.acme.com / Unsubscribes). Pass a stable
   * (memoized) array: a new one each render re-publishes the title.
   */
  trail?: ReadonlyArray<{ label: string; href: string }>
  /** Current entity label. Null clears the override while data is loading. */
  title: string | null
  /** Optional entity-level action rendered before the global header actions. */
  actions?: ReactNode
}

/**
 * Shared app-header foundation for collection → entity routes. It owns the
 * breadcrumb structure, canonical title styling, and PageTitleContext lifecycle
 * so detail pages never duplicate navigation chrome inside their page body.
 */
export function DetailPageHeader({
  parentLabel,
  parentHref,
  trail,
  title,
  actions = null,
}: DetailPageHeaderProps) {
  const setPageTitle = useSetPageTitle()

  const titleContent = useMemo(() => {
    if (title === null) {
      return null
    }

    return (
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="min-w-0 flex-nowrap gap-1.5">
          <BreadcrumbItem className="shrink-0">
            <BreadcrumbLink href={parentHref} className="font-medium">
              {parentLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {trail?.map((crumb) => (
            <Fragment key={crumb.href}>
              <BreadcrumbItem className="min-w-0">
                <BreadcrumbLink href={crumb.href} className="truncate">
                  {crumb.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          ))}
          <BreadcrumbItem className="min-w-0">
            <BreadcrumbPage className={cn(PAGE_TITLE_CLASSES, 'min-w-0')}>
              {title}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [parentHref, parentLabel, title, trail])

  useEffect(() => {
    setPageTitle({ titleContent, rightContent: title ? actions : null })
    return () => setPageTitle({ titleContent: null, rightContent: null })
  }, [actions, setPageTitle, title, titleContent])

  return null
}
